import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Typography,
  Tooltip,
} from '@mui/material';
import { Restore } from '@mui/icons-material';
import type { MappingVersion } from '@modules/offers/services/api';

interface VersionHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  versions: MappingVersion[];
  currentVersion: string;
  canAddEdit: boolean;
  onRestore: (version: MappingVersion) => void;
}

export const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
  open,
  onClose,
  versions,
  currentVersion,
  onRestore,
  canAddEdit,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Version History
        {currentVersion && <Chip label={`Current: v${currentVersion}`} size="small" color="primary" sx={{ ml: 2 }} />}
      </DialogTitle>
      <DialogContent>
        {versions.length === 0 ? (
          <Alert severity="info">No version history available.</Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Webhook URL</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((version) => (
                  <TableRow
                    key={version.version_id}
                    sx={{
                      backgroundColor: version.version === currentVersion ? 'action.selected' : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium">
                          v{version.version}
                        </Typography>
                        {version.version === currentVersion && <Chip label="Current" size="small" color="success" />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(version.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {version.created_by_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {version.webhook_url || '-'}
                      </Typography>
                    </TableCell>
                    {canAddEdit && (
                      <TableCell align="center">
                        <Tooltip title="Restore this version">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Restore />}
                              onClick={() => onRestore(version)}
                              disabled={version.version === currentVersion}
                            >
                              Restore
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
